import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Bell, MapPin, ShieldCheck, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { TaskProgress } from "@/components/TaskProgress";

export default function Perfil() {
  const { user, logout, loadMe } = useAuth();
  const nav = useNavigate();
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const savePin = async () => {
    if (pin.length !== 6 || !/^\d{6}$/.test(pin)) { toast.error("El PIN debe tener 6 dígitos"); return; }
    if (pin !== confirm) { toast.error("Los PIN no coinciden"); return; }
    setSaving(true);
    try {
      await api.post("/auth/pin", { pin, confirm });
      toast.success("PIN actualizado");
      setPin(""); setConfirm("");
      if (loadMe) loadMe();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const enablePush = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Permiso de notificaciones denegado"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const key = Uint8Array.from(atob(data.public_key.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      toast.success("Notificaciones activadas");
    } catch { toast.error("No se pudieron activar las notificaciones"); }
  };

  return (
    <div className="px-4 pt-5">
      <h1 className="font-display text-2xl font-semibold tracking-tight">Perfil</h1>
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 text-center">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-primary/10 text-2xl font-semibold text-primary">{user?.name?.[0]}</div>
        <p className="mt-3 font-display text-lg font-semibold">{user?.name}</p>
        <p className="text-sm text-slate-500">{user?.email || "Acceso por PIN"}</p>
        <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"><ShieldCheck className="h-3.5 w-3.5" /> {user?.role === "ADMIN" ? "Administrador" : "Operador"}</span>
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <p className="flex items-center gap-2 text-sm font-medium"><MapPin className="h-4 w-4 text-slate-400" /> Empresa</p>
        <p className="mt-1 text-sm text-slate-600">{user?.organization_name}</p>
      </div>

      <div className="mt-4"><TaskProgress /></div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <p className="flex items-center gap-2 text-sm font-medium"><KeyRound className="h-4 w-4 text-slate-400" /> PIN de acceso</p>
        <p className="mt-1 text-xs text-slate-500">{user?.has_pin ? "Ya tienes un PIN. Puedes cambiarlo." : "Aún no has configurado un PIN."} Se usa junto con tu correo para ingresar.</p>
        <div className="mt-3 space-y-2">
          <div>
            <Label className="text-xs text-slate-500">Nuevo PIN (6 dígitos)</Label>
            <Input data-testid="profile-pin-input" inputMode="numeric" maxLength={6} value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="mt-1 h-11 tnum tracking-widest" placeholder="••••••" type="password" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Confirmar PIN</Label>
            <Input data-testid="profile-pin-confirm" inputMode="numeric" maxLength={6} value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="mt-1 h-11 tnum tracking-widest" placeholder="••••••" type="password" />
          </div>
          <Button data-testid="profile-pin-save" disabled={saving} onClick={savePin} className="h-11 w-full rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar PIN"}
          </Button>
        </div>
      </div>

      <Button data-testid="enable-push-btn" variant="outline" onClick={enablePush} className="mt-4 h-12 w-full rounded-xl">
        <Bell className="mr-2 h-4 w-4" /> Activar notificaciones push
      </Button>
      <Button data-testid="user-logout-btn" variant="ghost" onClick={() => { logout(); nav("/login"); }} className="mt-2 h-12 w-full rounded-xl text-red-600 hover:bg-red-50 hover:text-red-700">
        <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
      </Button>
    </div>
  );
}
